import { useEffect, useMemo, useRef, useState } from "react";
import type { PhenotypeClip, PhenotypeTick } from "../api/gradient";

// Bearing-marker colours (match the app accent tokens).
const COL = {
  near: "#46d18b", // green ○  — nearest gate (the detector's pick)
  next: "#4a9eff", // blue  ○  — the next gate
  true: "#ff6b6b", // red   ○  — ground-truth bearing
  boresight: "rgba(200,200,200,0.5)",
};

// bearing [-1,1] → pixel coords in a w×h image. Retina convention: u=x, v=y,
// v=-1 is the TOP row. x = (u+1)/2·w, y = (v+1)/2·h.
function uvToPx(uv: [number, number], w: number, h: number): [number, number] {
  return [((uv[0] + 1) / 2) * w, ((uv[1] + 1) / 2) * h];
}

function fmtUV(uv: [number, number]): string {
  return `(${uv[0].toFixed(2)}, ${uv[1].toFixed(2)})`;
}

// Render one tick: the grayscale `frame` on the left canvas, retina `sal`iency
// (amber heat) on the right, both with bearing markers + a dotted boresight.
function drawTick(
  frameCanvas: HTMLCanvasElement,
  salCanvas: HTMLCanvasElement,
  clip: PhenotypeClip,
  tick: PhenotypeTick,
) {
  const [h, w] = clip.res;
  if (!h || !w) return;

  const paintChannel = (
    canvas: HTMLCanvasElement,
    data: number[],
    tint: (v: number) => [number, number, number],
  ) => {
    // Draw the h×w array into an offscreen ImageData, then nearest-neighbour
    // upscale it onto the display canvas.
    const src = document.createElement("canvas");
    src.width = w;
    src.height = h;
    const sctx = src.getContext("2d")!;
    const img = sctx.createImageData(w, h);
    for (let i = 0; i < w * h; i++) {
      const v = data[i] ?? 0;
      const [r, g, b] = tint(v);
      img.data[i * 4] = r;
      img.data[i * 4 + 1] = g;
      img.data[i * 4 + 2] = b;
      img.data[i * 4 + 3] = 255;
    }
    sctx.putImageData(img, 0, 0);

    const wrap = canvas.parentElement!;
    const dpr = window.devicePixelRatio || 1;
    // Keep the retina aspect ratio, fit within the host box.
    const boxW = wrap.clientWidth || 200;
    const boxH = wrap.clientHeight || 120;
    const scale = Math.min(boxW / w, boxH / h);
    const dispW = Math.max(1, Math.round(w * scale));
    const dispH = Math.max(1, Math.round(h * scale));
    canvas.width = dispW * dpr;
    canvas.height = dispH * dpr;
    canvas.style.width = `${dispW}px`;
    canvas.style.height = `${dispH}px`;
    const ctx = canvas.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, dispW, dispH);
    ctx.drawImage(src, 0, 0, dispW, dispH);
    drawMarkers(ctx, tick, dispW, dispH);
  };

  // grayscale frame (clamp to 0..1 for display)
  paintChannel(frameCanvas, tick.frame, (v) => {
    const g = Math.round(Math.max(0, Math.min(1, v)) * 255);
    return [g, g, g];
  });
  // saliency as amber heat (0 = dark, high = warm)
  paintChannel(salCanvas, tick.sal, (v) => {
    const t = Math.max(0, Math.min(1, v));
    return [Math.round(20 + t * 235), Math.round(10 + t * 170), Math.round(t * 40)];
  });
}

function drawMarkers(
  ctx: CanvasRenderingContext2D,
  tick: PhenotypeTick,
  dispW: number,
  dispH: number,
) {
  // dotted vertical boresight at image centre
  ctx.save();
  ctx.strokeStyle = COL.boresight;
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  ctx.beginPath();
  ctx.moveTo(dispW / 2, 0);
  ctx.lineTo(dispW / 2, dispH);
  ctx.stroke();
  ctx.restore();

  const r = Math.max(4, Math.min(dispW, dispH) * 0.04);
  const circle = (uv: [number, number], color: string) => {
    const [ux, uy] = uvToPx(uv, dispW, dispH);
    const inFrame = ux >= 0 && ux <= dispW && uy >= 0 && uy <= dispH;
    const x = Math.max(r, Math.min(dispW - r, ux));
    const y = Math.max(r, Math.min(dispH - r, uy));
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.globalAlpha = inFrame ? 1 : 0.4;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;
  };
  // draw true last so it sits on top
  circle(tick.next, COL.next);
  circle(tick.near, COL.near);
  circle(tick.true, COL.true);
}

export function GradientPhenotype({ clips }: { clips: PhenotypeClip[] }) {
  const [clipIdx, setClipIdx] = useState(0);
  const [tickIdx, setTickIdx] = useState(0);
  const [animate, setAnimate] = useState(false);
  const frameRef = useRef<HTMLCanvasElement | null>(null);
  const salRef = useRef<HTMLCanvasElement | null>(null);

  const safeClips = clips ?? [];
  const clip = safeClips[Math.min(clipIdx, safeClips.length - 1)] as PhenotypeClip | undefined;
  const ticks = clip?.ticks ?? [];
  const nTicks = ticks.length;
  const curTick = Math.min(tickIdx, Math.max(0, nTicks - 1));
  const tick = ticks[curTick];

  // clamp indices if the data shape changes underneath us
  useEffect(() => {
    if (clipIdx > safeClips.length - 1) setClipIdx(0);
  }, [safeClips.length, clipIdx]);
  useEffect(() => {
    if (tickIdx > nTicks - 1) setTickIdx(0);
  }, [nTicks, tickIdx]);

  // auto-advance ticks ~10 fps when animating
  useEffect(() => {
    if (!animate || nTicks < 2) return;
    const id = setInterval(() => setTickIdx((t) => (t + 1) % nTicks), 100);
    return () => clearInterval(id);
  }, [animate, nTicks]);

  // (re)draw whenever the selected clip/tick changes; keep in sync on resize
  useEffect(() => {
    const fc = frameRef.current;
    const sc = salRef.current;
    if (!fc || !sc || !clip || !tick) return;
    const render = () => drawTick(fc, sc, clip, tick);
    render();
    const ro = new ResizeObserver(render);
    if (fc.parentElement) ro.observe(fc.parentElement);
    return () => ro.disconnect();
  }, [clip, tick]);

  const err = useMemo(() => {
    if (!tick) return null;
    return Math.hypot(tick.near[0] - tick.true[0], tick.near[1] - tick.true[1]);
  }, [tick]);

  if (safeClips.length === 0) return <div className="viz-empty">no phenotype clips</div>;

  return (
    <div className="pheno mono">
      <div className="pheno-tabs">
        {safeClips.map((_, i) => (
          <button
            key={i}
            className={i === clipIdx ? "on" : ""}
            onClick={() => {
              setClipIdx(i);
              setTickIdx(0);
            }}
          >
            clip {i + 1}
          </button>
        ))}
        <button
          className={`pheno-play${animate ? " on" : ""}`}
          onClick={() => setAnimate((a) => !a)}
          disabled={nTicks < 2}
        >
          {animate ? "❚❚ pause" : "▶ play"}
        </button>
      </div>

      <div className="pheno-frames">
        <div className="pheno-cell">
          <span className="pheno-cell-lbl dim">frame</span>
          <div className="pheno-canvas-host">
            <canvas ref={frameRef} />
          </div>
        </div>
        <div className="pheno-cell">
          <span className="pheno-cell-lbl dim">saliency</span>
          <div className="pheno-canvas-host">
            <canvas ref={salRef} />
          </div>
        </div>
      </div>

      <input
        className="pheno-scrub"
        type="range"
        min={0}
        max={Math.max(0, nTicks - 1)}
        value={curTick}
        disabled={nTicks < 2}
        onChange={(e) => {
          setAnimate(false);
          setTickIdx(+e.target.value);
        }}
      />

      <div className="pheno-readout">
        <span>
          tick <b>{nTicks ? curTick + 1 : 0}</b> / {nTicks}
        </span>
        <span>
          vis <b>{tick ? `${(tick.vis * 100).toFixed(1)}%` : "—"}</b>
        </span>
        <span style={{ color: COL.near }}>near {tick ? fmtUV(tick.near) : "—"}</span>
        <span>
          err <b>{err != null ? err.toFixed(3) : "—"}</b>
        </span>
      </div>
    </div>
  );
}
